import axios from 'axios';
import { refreshAccessToken } from 'utils/api-client';
import { getAccessToken, getRefreshToken } from 'utils/sessionStore';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

// Request interceptor: attach auth header, proactively refresh if token is missing
axiosInstance.interceptors.request.use(async (config) => {
  let token = getAccessToken();

  // If no access token but refresh token exists, proactively refresh
  if (!token && getRefreshToken()) {
    try {
      token = await refreshAccessToken();
    } catch {
      // Refresh failed — request will proceed without auth, server will return 401
    }
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: handle 401 with automatic token refresh and retry
axiosInstance.interceptors.response.use(
  (response) => (response.data.data ? response.data.data : response.data),
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retried && getRefreshToken()) {
      originalRequest._retried = true;

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch {
        // refreshAccessToken already handles clearTokens + redirect
      }
    }

    return Promise.reject({
      status: error.response?.status,
      data: error.response?.data || error.message,
    });
  },
);

export default axiosInstance;
